import isUUID from 'validator/lib/isUUID';
import Vue from 'vue';

import router from '@core/router';
import { findRouteByName } from '@core/router/utils';
import { snack } from '@core/services/ui';
import store from '@core/store';
import { Auth } from '@core/auth';

import OrganisationTypeEnum from '@constants/enums/organisation/type';

import i18n from '@plugins/i18n';

import { Organisation, Investor, Adviser, Provider, FundManager, Company } from '@/models';

export const organisation_models = {
    INVESTOR: Investor,
    ADVISER: Adviser,
    PRODUCT_PROVIDER: Provider,
    FUND_MANAGER: FundManager,
    COMPANY: Company
};

const getInitialState = () => ({
    loading: false,
    loaded: false,
    saving: [],
    organisation: new Organisation(),
    organisation_id: null,
    organisation_type: null,
    organisation_model: Organisation,
    organisation_hydrates: {},
    organisation_payload: {},
    organisation_validation_results: {},
    tabs: [],
    payload_data: {},
    target_route: null
});

export default {
    settings: {
        namespaced: true,
        state: getInitialState(),
        getters: {
            loading: state => state.loading,
            loaded: state => state.loaded,
            saving: state => state.saving,
            organisation: state => state.organisation || new Organisation(),
            organisation_id: state => state.organisation_id,
            organisation_type: state => state.organisation_type,
            organisation_model: state => state.organisation_model,
            organisation_payload: state => state.organisation_payload,
            organisation_validation_results: state => state.organisation_validation_results,
            organisation_hydrates: state => state.organisation_hydrates,
            organisation_is_me: state => {
                if (!state.organisation) {
                    return false;
                }

                const auth = Auth();

                return state.organisation.id === auth.organisation_id;
            },
            target_route: state => state.target_route,
            payload: state => {
                const handler = {
                    get: (target, property) => {
                        // Return a value from the payload if it exists, otherwise from the organisation
                        return Reflect.has(target, property)
                            ? target[`${property}`]
                            : state.organisation[`${property}`];
                    },
                    set: (target, property, value) => {
                        // Use Vue.set to ensure reactivity when adding new properties
                        Vue.set(target, property, value);
                        return true;
                    }
                };
                return new Proxy(state.payload_data, handler);
            },
            tabs: state => {
                if (!state.tabs) {
                    return [];
                }

                const badges = Object.keys(state.organisation_validation_results).reduce((badges, validationKey) => {
                    badges[`${validationKey}`] = Object.values(
                        state.organisation_validation_results[`${validationKey}`]
                    ).filter(res => res !== true).length;
                    return badges;
                }, {});

                const tabs = state.tabs.map(tab => {
                    tab.badge = tab.to.name in badges ? badges[`${tab.to.name}`] : null;
                    tab.loading = state.loading && state.target_route?.name === tab.to.name;

                    return tab;
                });

                return tabs;
            }
        },
        actions: {
            async setup({ commit, state, dispatch }, route) {
                commit('SET_LOADED', false);
                commit('RESET_PAYLOAD');
                commit('SET_LOADING', true);
                commit('SET_TARGET_ROUTE', route);

                const auth = Auth();

                let organisation_id = route.query.organisation_id;
                let organisation_type = route.query.organisation_type
                    ? route.query.organisation_type.toUpperCase()
                    : undefined;

                if (organisation_id && !isUUID(organisation_id)) {
                    throw new Error(`Organisation ID "${organisation_id}" is not a valid UUID`);
                }

                if (organisation_type && !(organisation_type.toUpperCase() in OrganisationTypeEnum)) {
                    throw new Error(`Organisation type "${organisation_type}" does not exist`);
                }

                if (organisation_id && !organisation_type) {
                    throw new Error('Organisation type missing');
                }

                if (auth.is_investor && organisation_id && organisation_id !== auth.organisation_id) {
                    throw new Error('You do not have permission to view this organisation');
                }

                if (!organisation_id) {
                    organisation_id = auth.organisation_id;
                    organisation_type = auth.organisation_type;
                }

                commit('SET_ORGANISATION_ID', organisation_id);
                commit('SET_ORGANISATION_TYPE', organisation_type);
                commit('SET_ORGANISATION_MODEL', organisation_type);

                await dispatch('setOrganisation');

                dispatch('setTabs', route);
                if (!state.tabs.map(tab => tab.id).includes(route.name)) {
                    const error = new Error(`Invalid route navigation to ${route.name}`);
                    error.redirect = state.tabs[0].to;

                    if (state.organisation) {
                        const default_tab = state.tabs.find(tab => tab.id === state.organisation.default_settings_tab);
                        if (default_tab) {
                            error.redirect = default_tab.to;
                        }
                    }

                    if (error.redirect.name === router.currentRoute.name) {
                        commit('SET_LOADING', false);
                        commit('SET_LOADED', true);
                    }

                    throw error;
                }

                dispatch('afterSetup');
            },
            async afterSetup({ commit, getters, dispatch }) {
                commit('SET_LOADING', true);

                await dispatch('hydrateOrganisation');

                if (Auth().has_permission('INVESTOR_UPDATE') || getters.organisation_is_me) {
                    await dispatch('validateOrganisation');
                }

                commit('SET_LOADING', false);
                commit('SET_LOADED', true);
            },
            setTabs({ commit, state }, route) {
                if (!state.organisation) {
                    return [];
                }

                const settings = findRouteByName('Settings');

                if (!settings || !settings.children) {
                    return [];
                }

                route = route || router.currentRoute;

                const auth = Auth();

                const routes = (settings.children || []).filter(r => {
                    if (typeof r.meta.active === 'function') {
                        return r.meta.active(state.organisation, auth, route);
                    } else if (typeof r.meta.active === 'boolean') {
                        return r.meta.active;
                    }
                    return true;
                });

                if (routes.length === 0) {
                    return [];
                }

                const query = Object.keys(route.query).reduce((acc, key) => {
                    if (key !== 'force_recategorise') {
                        acc[`${key}`] = route.query[`${key}`];
                    }
                    return acc;
                }, {});

                const tabs = routes.map(r => ({
                    id: r.name,
                    name: r.meta.heading,
                    to: { name: r.name, query },
                    badge: null,
                    loading: false
                }));

                commit('SET_TABS', tabs);
            },
            async setOrganisation({ commit, state }, options = {}) {
                options = {
                    force: false,
                    ...options
                };

                const { force } = options;

                let newOrganisation = null;

                try {
                    newOrganisation = await state.organisation_model.$get(state.organisation_id, {
                        type: 'FULL',
                        force
                    });
                } catch (error) {
                    if (error?.code === 402) {
                        newOrganisation = await state.organisation_model.find(state.organisation_id);
                        if (!newOrganisation) {
                            newOrganisation = await state.organisation_model.$index({
                                first: true,
                                cast: true,
                                filters: { id: state.organisation_id }
                            });
                        }
                    } else {
                        throw error;
                    }
                }

                // Re-apply existing hydrated data to the new object before committing
                for (const hydrateKey in state.organisation_hydrates) {
                    if (state.organisation_hydrates[`${hydrateKey}`].data) {
                        newOrganisation[`${hydrateKey}`] = state.organisation_hydrates[`${hydrateKey}`].data;
                    }
                }

                commit('SET_ORGANISATION', newOrganisation);
                commit('RESET_ORGANISATION_HYDRATES');
            },
            async hydrate({ commit, state }, options) {
                options = {
                    key: null,
                    force: false,
                    type: 'FULL',
                    ...options
                };

                const { key, force, type } = options;

                if (!key || !(key in state.organisation_hydrates)) {
                    return;
                }

                let { data, active } = state.organisation_hydrates[`${key}`];

                if (typeof active === 'function') {
                    active = await active(state);
                } else if (typeof active !== 'boolean') {
                    active = true;
                }

                try {
                    if (active && (force || !data)) {
                        data = await state.organisation_hydrates[`${key}`].request({ force, type });
                        commit('SET_ORGANISATION_HYDRATE_DATA', { key, data, error: null });
                    }
                } catch (error) {
                    console.error(error);
                    commit('SET_ORGANISATION_HYDRATE_DATA', { key, data: null, error: error.message });
                }

                return data;
            },
            async hydrateOrganisation({ state, dispatch }, hydrateKeys = []) {
                const requests = [];

                const keys = Object.keys(state.organisation_hydrates).filter(
                    key => hydrateKeys.length === 0 || hydrateKeys.includes(key)
                );

                for (const hydrateKey of keys) {
                    requests.push(dispatch('hydrate', { key: hydrateKey, force: false }));
                }

                await Promise.all(requests);
            },
            async validateOrganisation({ commit, state }) {
                if (!state.organisation) {
                    return;
                }
                commit('SET_ORGANISATION_VALIDATION_RESULTS', state.organisation.$validate());
            },
            async updateOrganisation({ commit, state, dispatch }, options = {}) {
                options = {
                    payload: {},
                    callback: org => org,
                    success_text: i18n.t('organisation') + ' ' + i18n.t('update_successful').toLowerCase(),
                    ...options
                };

                const { payload, callback, success_text } = options;

                commit('SET_SAVING', Object.keys(payload));

                try {
                    if (!payload || Object.keys(payload).length === 0) {
                        snack({
                            id: 'update_organisation',
                            timeout: 5000,
                            type: 'success',
                            text: success_text
                        });
                    } else {
                        await state.organisation.$update(payload);
                        await dispatch('setOrganisation', { force: true });
                        await dispatch('validateOrganisation');
                        commit('RESET_PAYLOAD');

                        snack({
                            id: 'update_organisation',
                            timeout: 5000,
                            type: 'success',
                            text: success_text
                        });

                        return callback();
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    commit('SET_SAVING', []);
                }
            }
        },
        mutations: {
            SET_PAYLOAD_DATA_VALUE(state, payload) {
                Vue.set(state.payload_data, payload.key, payload.value);
            },
            SET_LOADING(state, payload) {
                state.loading = payload;
            },
            SET_LOADED(state, payload) {
                state.loaded = payload;
            },
            SET_SAVING(state, payload) {
                state.saving = payload;
            },
            SET_ORGANISATION_ID(state, payload) {
                state.organisation_id = payload;
            },
            SET_ORGANISATION_TYPE(state, payload) {
                state.organisation_type = payload;
            },
            SET_ORGANISATION_MODEL(state, payload) {
                state.organisation_model = organisation_models[`${payload}`];
            },
            CLEAR_ORGANISATION_HYDRATES(state) {
                state.organisation_hydrates = {};
            },
            RESET_ORGANISATION_HYDRATES(state) {
                if (!state.organisation) {
                    state.organisation_hydrates = {};
                    return;
                }

                if (state.organisation.is_investor) {
                    const auth = Auth();

                    if (!('users' in state.organisation_hydrates)) {
                        state.organisation_hydrates.users = {
                            active: !auth.is_fund_manager && !auth.is_provider,
                            request: (...params) => state.organisation.$getUsers(...params),
                            data: null,
                            error: null
                        };
                    }
                    if (!('adviser_accounts' in state.organisation_hydrates)) {
                        state.organisation_hydrates.adviser_accounts = {
                            active: !auth.is_fund_manager && !auth.is_provider,
                            request: (...params) => state.organisation.$getAdviserAccounts(...params),
                            data: null,
                            error: null
                        };
                    }
                    if (!('accounts' in state.organisation_hydrates)) {
                        state.organisation_hydrates.accounts = {
                            active: !auth.is_fund_manager && !auth.is_provider,
                            request: (...params) => state.organisation.$getAccounts(...params),
                            data: null,
                            error: null
                        };
                    }
                    if (!('appropriateness' in state.organisation_hydrates)) {
                        state.organisation_hydrates.appropriateness = {
                            request: (...params) => state.organisation.$getAppropriateness(...params),
                            data: null,
                            error: null
                        };
                    }
                    if (!('additional_needs' in state.organisation_hydrates)) {
                        state.organisation_hydrates.additional_needs = {
                            request: (...params) => state.organisation.$getAdditionalNeeds(...params),
                            data: null,
                            error: null
                        };
                    }
                }
            },
            SET_ORGANISATION_HYDRATE_DATA(state, payload) {
                const { key, data, error } = payload;
                const oldHydrate = state.organisation_hydrates[`${key}`];
                Vue.set(state.organisation_hydrates, key, {
                    ...oldHydrate,
                    data,
                    error: error !== undefined ? error : oldHydrate.error
                });

                if (state.organisation) {
                    Vue.set(state.organisation, key, data);
                }
            },
            SET_ORGANISATION(state, payload) {
                state.organisation = payload;
                state.organisation_payload = payload instanceof Organisation ? payload.$toPayload() : {};

                if (state.organisation.is_investor) {
                    store.dispatch('app/addQuerySelector', {
                        key: 'investor_id',
                        value: state.organisation.id,
                        data: state.organisation
                    });
                }
            },
            SET_ORGANISATION_VALIDATION_RESULTS(state, payload) {
                state.organisation_validation_results = payload;
            },
            SET_TABS(state, payload) {
                state.tabs = payload;
            },
            RESET_PAYLOAD(state) {
                state.payload_data = {};
            },
            SET_TARGET_ROUTE(state, payload) {
                state.target_route = payload;
            },
            RESET_STATE(state) {
                Object.assign(state, getInitialState());
            }
        }
    }
};
